import { type ReactNode, useState } from 'react';
import { CancelScheduledShiftPopup } from '~/components/ShiftDetailsRedesign/ScheduledShiftDetailsPage/components/CancelScheduledShiftPopup';
import { type ICancelJobPayload, useCancelJobMutation, useGetJob } from '~/domain/Jobs';
import { getStandardCancelFlow } from '~/services/posthog/FeatureFlagUtils';
import { SHIFT_CARD_STATUS, type TShiftCardData } from '~/types/ShiftCard';
import { CancelShiftConfirmPopup } from './CancelShiftConfirmPopup';
import { CancelShiftLoadingDialog } from './CancelShiftLoadingDialog';

export interface IUseShiftCardCancelArgs {
  jobId: string;
  status: TShiftCardData['status'];
  isInterview: boolean;
  date?: ReactNode;
  /** Runs once the cancel succeeds, e.g. to drop the card from a Redux-backed list. */
  onCancelled?: () => void;
}

/** Card-menu Cancel: the standard flow for the `treatment` variant, else the plain popup. */
export const useShiftCardCancel = ({
  jobId,
  status,
  isInterview,
  date,
  onCancelled,
}: IUseShiftCardCancelArgs) => {
  // The variant as it was when the popup opened (null = closed), so flags landing late can't
  // swap the popup or the payload under the cursor.
  const [openedWithV3, setOpenedWithV3] = useState<boolean | null>(null);
  const isOpen = openedWithV3 !== null;
  const { mutate: cancelJob, isPending } = useCancelJobMutation();

  const showsRefund = openedWithV3 === true && status === SHIFT_CARD_STATUS.SCHEDULED;
  // Not the card's own job: the list endpoints' cancellation_fee / total_refund disagree
  // with GET /jobs/:id on dev (GOTU-15983, 2026-10-19), and the popup must show the real fee.
  const {
    data: job,
    isError,
    isFetching,
    refetch,
  } = useGetJob(jobId, { enabled: isOpen && showsRefund });

  const close = () => setOpenedWithV3(null);

  const confirm = () => {
    setOpenedWithV3(null);
    const payload: ICancelJobPayload = openedWithV3 ? { jobId, redirect: true } : { jobId };
    if (onCancelled) {
      cancelJob(payload, { onSuccess: onCancelled });
    } else {
      cancelJob(payload);
    }
  };

  // The refund popup waits out any refetch of a cached job, and a failed one shows the error,
  // so the fee it shows is current.
  let cancelPopup: ReactNode = null;
  if (isOpen && !showsRefund) {
    cancelPopup = (
      <CancelShiftConfirmPopup
        isInterview={isInterview}
        date={date}
        isPending={isPending}
        onClose={close}
        onConfirm={confirm}
      />
    );
  } else if (isOpen && job && !isFetching && !isError) {
    cancelPopup = (
      <CancelScheduledShiftPopup
        job={job}
        isPending={isPending}
        onClose={close}
        onConfirm={confirm}
      />
    );
  } else if (isOpen) {
    cancelPopup = (
      <CancelShiftLoadingDialog
        isInterview={isInterview}
        isError={isError && !isFetching}
        onClose={close}
        onRetry={() => void refetch()}
      />
    );
  }

  // Read on the tap, not per render: records the A/B exposure and adds no per-card flag listener.
  const openCancelPopup = () => setOpenedWithV3(getStandardCancelFlow());

  return { openCancelPopup, cancelPopup };
};
